import {
  getMediaTypeFromUrl,
  is3DContentType,
  is3DModelUrl,
  resolveIs3D,
  type MediaType,
} from './mediaTypeDetection';

export type GallerySelectableMediaType = MediaType | 'model';

export interface GalleryMediaSource {
  contentType?: string;
  mediaType?: GallerySelectableMediaType;
  resolvedUrl?: string;
  original_urls?: string;
  preview_urls?: string;
  external_urls?: string;
}

function parseUrls(value?: string): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((url): url is string => typeof url === 'string' && url.length > 0)
      : [];
  } catch {
    return [];
  }
}

function isExtensionlessUrl(url: string): boolean {
  try {
    const fileName = new URL(url, 'https://local.invalid').pathname.split('/').pop() || '';
    return !fileName.includes('.');
  } catch {
    return false;
  }
}

function getUrlExtension(url: string): string | null {
  try {
    const fileName = new URL(url, 'https://local.invalid').pathname.split('/').pop() || '';
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex > 0 ? fileName.slice(dotIndex + 1).toLowerCase() : null;
  } catch {
    return null;
  }
}

export function getGalleryMediaType(
  contentType?: string,
  url?: string
): GallerySelectableMediaType {
  if (resolveIs3D(contentType, url)) return 'model';
  if (contentType?.startsWith('video')) return 'video';
  if (contentType?.startsWith('audio')) return 'audio';
  return getMediaTypeFromUrl(url || '');
}

/** Resolve the actual selectable asset, preferring a model over its poster/texture companions. */
export function resolveGalleryMedia(
  source: GalleryMediaSource,
  acceptedModelFormats?: readonly string[]
): {
  url: string;
  previewUrl: string;
  mediaType: GallerySelectableMediaType;
} {
  const originals = parseUrls(source.original_urls);
  const previews = parseUrls(source.preview_urls);
  const externals = parseUrls(source.external_urls);
  const effectiveContentType = source.contentType
    || (source.mediaType === 'model' ? '3d' : source.mediaType);
  const fallbackUrl = source.resolvedUrl || originals[0] || previews[0] || externals[0] || '';
  const modelCandidates = [source.resolvedUrl, ...originals, ...externals, ...previews]
    .filter((url): url is string => !!url);
  const allowedModelExtensions = acceptedModelFormats?.length
    ? new Set(acceptedModelFormats.map(format => format.replace(/^\./, '').toLowerCase()))
    : null;
  const modelUrl = modelCandidates.find(url => {
    if (!is3DModelUrl(url)) return false;
    const extension = getUrlExtension(url);
    return !allowedModelExtensions || (!!extension && allowedModelExtensions.has(extension));
  }) || modelCandidates.find(url =>
    (!allowedModelExtensions || allowedModelExtensions.has('glb'))
      && isExtensionlessUrl(url)
      && resolveIs3D(effectiveContentType, url)
  );
  const hasModelAsset = modelCandidates.some(url =>
    is3DModelUrl(url) || (isExtensionlessUrl(url) && resolveIs3D(effectiveContentType, url))
  );
  if (allowedModelExtensions && hasModelAsset && !modelUrl) {
    return { url: '', previewUrl: '', mediaType: 'model' };
  }
  const resolvedUrl = modelUrl || fallbackUrl;
  const mediaType = modelUrl
    ? 'model'
    : getGalleryMediaType(
        is3DContentType(effectiveContentType) ? undefined : effectiveContentType,
        resolvedUrl
      );

  return {
    url: resolvedUrl,
    previewUrl: mediaType === 'model' ? resolvedUrl : (previews[0] || resolvedUrl),
    mediaType,
  };
}
